import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLanguageSync } from "../../translations/useLanguageSync";
import { FLAG_COMPONENTS } from "../common/flags/FlagIcons";
import styles from "./languageSelector.module.scss";
import classNames from "classnames/bind";

const cx = classNames.bind(styles);

const SUPPORTED_LANGUAGES = [
  { code: "en", name: "English" },
  { code: "de", name: "Deutsch" },
  { code: "uk", name: "Українська" },
  { code: "ru", name: "Русский" },
];

export const LanguageSelector: React.FC = () => {
  const { t } = useTranslation();
  const { currentLanguage, changeLanguage, isUpdating } = useLanguageSync();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const currentLang =
    SUPPORTED_LANGUAGES.find((lang) => lang.code === currentLanguage) ||
    SUPPORTED_LANGUAGES[0];

  const closeDropdown = () => setIsOpen(false);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        closeDropdown();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeDropdown();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  const handleLanguageSelect = (langCode: string) => {
    changeLanguage(langCode);
    closeDropdown();
  };

  const handleKeyDown = (event: React.KeyboardEvent, action: () => void) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      action();
    }
  };

  return (
    <div className={styles.languageSelector} ref={ref}>
      <button
        className={cx(
          styles.languageSelector__btn,
          isOpen ? styles.isOpen : "",
          isUpdating ? styles.isUpdating : ""
        )}
        onClick={() => setIsOpen(!isOpen)}
        disabled={isUpdating}
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={t("languageSelector.ariaLabel", "Current language: {{lang}}. Click to change language.", { lang: currentLang.name })}
      >
        <span className={styles.languageSelector__flag}>
          {FLAG_COMPONENTS[currentLang.code]?.({ size: 20 })}
        </span>
        <span className={styles.languageSelector__code}>
          {currentLang.code.toUpperCase()}
        </span>
        <span
          className={cx(
            styles.languageSelector__arrow,
            isOpen ? styles.isOpen : ""
          )}
        >
          ▼
        </span>
      </button>

      {isOpen && (
        <div className={styles.languageSelector__dropdown} role="listbox">
          {SUPPORTED_LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              className={cx(
                styles.languageSelector__option,
                lang.code === currentLanguage ? styles.isSelected : ""
              )}
              onClick={() => handleLanguageSelect(lang.code)}
              onKeyDown={(e) =>
                handleKeyDown(e, () => handleLanguageSelect(lang.code))
              }
              type="button"
              role="option"
              aria-selected={lang.code === currentLanguage}
            >
              <span className={styles.languageSelector__flag}>{FLAG_COMPONENTS[lang.code]?.({ size: 20 })}</span>
              <span className={styles.languageSelector__name}>{lang.name}</span>
              {lang.code === currentLanguage && (
                <span className={styles.languageSelector__checkmark}>✓</span>
              )}
            </button>
          ))}
        </div>
      )}

      {isUpdating && (
        <div className={styles.languageSelector__loading}>
          <div className={styles.languageSelector__spinner}></div>
        </div>
      )}
    </div>
  );
};
